/* ==============================================================
   UPSELL POPUP — VANILLA JS (no jQuery, no third-party app)
   ------------------------------------------------------------
   Flow:
   1. We do NOT intercept or duplicate the theme's own "Add to
      Cart" click/submit — the theme (liquid-handlers.js) already
      adds the product reliably. Duplicating that call was causing
      double-quantity adds.
   2. Instead, we PASSIVELY watch the network layer (fetch + XHR)
      for any successful call to /cart/add(.js) — no matter who
      made it — and react to it: suppress the Smart Cart drawer,
      open our popup, and load recommendations.
   3. Fetch recommended products via the Product Recommendations
      API and render them with checkboxes.
   4. Skip -> reveal the Smart Cart, untouched, no extra items added.
      Continue -> add selected items ourselves, THEN reveal the
      Smart Cart (only after the cart state has actually refreshed).

   The Smart Cart app itself is never modified, unbound, or
   replaced — we simply control *when it's visible*, by directly
   toggling its own (Shadow DOM) container.
   ============================================================== */

(function () {
  'use strict';

  // Visible marker — open DevTools Console and reload the product
  // page. If you DON'T see this line, the browser/CDN is still
  // serving an old cached file — that's the actual problem, not
  // the logic below.
  console.log('[Upsell Popup] v3 script running');

  // Guard against the script accidentally running twice (e.g. if the
  // snippet ends up rendered in two places in the theme) — that would
  // double every event listener and double every add-to-cart quantity.
  if (window.__upsellPopupLoaded) return;
  window.__upsellPopupLoaded = true;

  var config = window.upsellPopupConfig || {};

  // Only wire anything up when we actually have a product id
  // (i.e. we're rendering in a product-page context).
  if (!config.currentProductId) return;

  // ----------------------------------------------------------
  // STATE
  // ----------------------------------------------------------
  var state = {
    isContinuing: false,     // guards the "Continue" bulk-add request
    recommendedProducts: [], // raw API response
    selectedVariantIds: new Set()
  };

  // ----------------------------------------------------------
  // DOM REFS (queried once DOM is ready)
  // ----------------------------------------------------------
  var els = {};

  function cacheEls() {
    els.overlay = document.getElementById('upsell-popup-overlay');
    els.modal = document.getElementById('upsell-popup');
    els.closeBtn = document.getElementById('upsell-close-btn');
    els.addedProduct = document.getElementById('upsell-added-product');
    els.loading = document.getElementById('upsell-loading');
    els.errorEl = document.getElementById('upsell-error');
    els.list = document.getElementById('upsell-product-list');
    els.skipBtn = document.getElementById('upsell-skip-btn');
    els.continueBtn = document.getElementById('upsell-continue-btn');
    els.continueLabel = els.continueBtn.querySelector('.upsell-btn-label');
    els.continueSpinner = els.continueBtn.querySelector('.upsell-btn-spinner');

    // Belt-and-braces: force this hidden via JS too, in case the
    // liquid markup's `hidden` attribute didn't make it into the
    // theme (never want this text visible, per request). Paired
    // with a CSS [hidden]{display:none!important} rule in upsell.css
    // so no CSS specificity can override it and re-show it.
    if (els.loading) els.loading.hidden = true;
  }

  // ----------------------------------------------------------
  // PASSIVE CART-ADD DETECTION
  // Wraps window.fetch and XMLHttpRequest just enough to notice
  // when a request to /cart/add(.js) succeeds. We never block or
  // alter that request — we let it run exactly as the theme
  // intended, and just react once it's done. This is what stops
  // the earlier "quantity doubled" bug: we no longer submit a
  // second add-to-cart of our own for the customer's original click.
  //
  // Our OWN "Continue" bulk-add call is tagged with a custom
  // header so this watcher ignores it (it's already handled by
  // its own success/error callbacks in addSelectedProducts()).
  // ----------------------------------------------------------
  var OWN_REQUEST_HEADER = 'X-Upsell-Popup';

  function isCartAddUrl(url) {
    return /\/cart\/add(\.js)?(\?|$)/.test(url || '');
  }

  function onExternalCartAddSuccess(item) {
    if (!item || item.status) return; // Shopify error responses have a "status" field
    suppressSmartCart(); // in case it hasn't already started
    refreshCartState();
    renderAddedProduct(item);
    openPopup();
    loadRecommendations();
  }

  function initCartAddWatcher() {
    // --- fetch() ---
    var originalFetch = window.fetch;
    if (typeof originalFetch === 'function') {
      window.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var isOwn = !!(init && init.headers && init.headers[OWN_REQUEST_HEADER]);
        var isAdd = isCartAddUrl(url);

        if (isAdd && !isOwn) {
          // Start suppressing the drawer the instant the request goes
          // out — don't wait for the response — so we beat any
          // network-level auto-open race.
          suppressSmartCart();
        }

        var result = originalFetch.apply(window, arguments);

        if (isAdd && !isOwn) {
          result
            .then(function (res) {
              return res
                .clone()
                .json()
                .then(function (data) {
                  if (res.ok) onExternalCartAddSuccess(data);
                  else releaseSmartCart(); // add failed — don't leave the drawer stuck hidden
                });
            })
            .catch(function () {
              /* Not JSON, or network error — ignore, not our concern */
            });
        }

        return result;
      };
    }

    // --- XMLHttpRequest (fallback for themes that use XHR instead of fetch) ---
    var OriginalXHR = window.XMLHttpRequest;
    if (OriginalXHR) {
      var originalOpen = OriginalXHR.prototype.open;
      var originalSend = OriginalXHR.prototype.send;

      OriginalXHR.prototype.open = function (method, url) {
        this.__isCartAdd = isCartAddUrl(url);
        return originalOpen.apply(this, arguments);
      };

      OriginalXHR.prototype.send = function () {
        if (this.__isCartAdd) {
          suppressSmartCart();
          this.addEventListener('load', function () {
            try {
              var data = JSON.parse(this.responseText);
              if (this.status >= 200 && this.status < 300) onExternalCartAddSuccess(data);
              else releaseSmartCart();
            } catch (err) {
              /* Not JSON — ignore */
            }
          });
        }
        return originalSend.apply(this, arguments);
      };
    }
  }

  // ----------------------------------------------------------
  // ADDED-PRODUCT CARD
  // Populated directly from the /cart/add.js response — no extra
  // API call needed, since that response already contains the
  // product title, image, price and quantity.
  // ----------------------------------------------------------
  function renderAddedProduct(item) {
    if (!item) {
      els.addedProduct.hidden = true;
      return;
    }

    var imgSrc = item.image || '';
    var qtyText = item.quantity > 1 ? item.quantity + ' × ' : '';

    els.addedProduct.innerHTML =
      '<img class="upsell-added-product-image" src="' +
      imgSrc +
      '" alt="' +
      escapeHtml(item.product_title || item.title) +
      '" loading="lazy">' +
      '<div class="upsell-added-product-info">' +
      '<p class="upsell-added-product-title">' + escapeHtml(item.product_title || item.title) + '</p>' +
      '<p class="upsell-added-product-meta">' + qtyText + formatMoney(item.price) + '</p>' +
      '</div>' +
      '<span class="upsell-added-product-badge">✓ Added</span>';

    els.addedProduct.hidden = false;
  }

  // ----------------------------------------------------------
  // POPUP OPEN / CLOSE
  // ----------------------------------------------------------
  function openPopup() {
    els.overlay.classList.add('is-open');
    els.overlay.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
  }

  function closePopup() {
    els.overlay.classList.remove('is-open');
    els.overlay.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    releaseSmartCart();
  }

  // ----------------------------------------------------------
  // RECOMMENDATIONS
  // ----------------------------------------------------------
  function loadRecommendations() {
    resetRecommendationUI();

    var url =
      config.recommendationsUrl +
      '?product_id=' +
      encodeURIComponent(config.currentProductId) +
      '&limit=' +
      encodeURIComponent(config.recommendationsLimit || 6);

    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('Recommendations request failed');
        return res.json();
      })
      .then(function (data) {
        var products = (data && data.products) || [];
        state.recommendedProducts = products;
        renderProductList(products);
      })
      .catch(function (err) {
        console.error('[Upsell] Recommendations failed:', err);
        showError();
      });
  }

  // Loading text is intentionally never shown (per request) — the
  // element stays `hidden` the whole time; list just pops in once ready.
  function resetRecommendationUI() {
    els.errorEl.hidden = true;
    els.list.hidden = true;
    els.list.innerHTML = '';
    state.selectedVariantIds.clear();
    updateContinueLabel();
  }

  function showError() {
    els.errorEl.hidden = false;
    els.list.hidden = true;
  }

  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    if (config.moneyFormat && config.moneyFormat.indexOf('{{amount}}') > -1) {
      return config.moneyFormat.replace('{{amount}}', amount);
    }
    return amount;
  }

  // How many recommended items are pre-checked by default when the
  // popup opens (customer can uncheck any of them). Set to 0 to have
  // everything start unchecked.
  var DEFAULT_CHECKED_COUNT = 2;

  function renderProductList(products) {
    if (!products.length) {
      showError();
      return;
    }

    var frag = document.createDocumentFragment();

    products.forEach(function (product, index) {
      var variant = product.variants && product.variants[0];
      if (!variant) return;

      var row = document.createElement('div');
      row.className = 'upsell-product';
      row.setAttribute('role', 'checkbox');
      row.setAttribute('aria-checked', 'false');
      row.setAttribute('tabindex', '0');
      row.dataset.variantId = variant.id;

      var imgSrc = product.featured_image || (product.images && product.images[0]) || '';

      var comparePrice = variant.compare_at_price;
      var onSale = comparePrice && Number(comparePrice) > Number(variant.price);
      var priceHtml = onSale
        ? '<span class="upsell-product-price upsell-product-price--discounted">' + formatMoney(variant.price) + '</span>' +
          '<span class="upsell-product-compare-price">' + formatMoney(comparePrice) + '</span>' +
          '<span class="upsell-discount-badge">' +
          Math.round((1 - variant.price / comparePrice) * 100) +
          '% OFF</span>'
        : '<span class="upsell-product-price">' + formatMoney(variant.price) + '</span>';

      row.innerHTML =
        '<img class="upsell-product-image" src="' +
        imgSrc +
        '" alt="' +
        escapeHtml(product.title) +
        '" loading="lazy">' +
        '<div class="upsell-product-info">' +
        '<p class="upsell-product-title">' + escapeHtml(product.title) + '</p>' +
        '<div class="upsell-product-price-row">' + priceHtml + '</div>' +
        '</div>' +
        '<span class="upsell-checkbox">' +
        '<svg width="12" height="9" viewBox="0 0 12 9" fill="none" xmlns="http://www.w3.org/2000/svg">' +
        '<path d="M1 4.5L4.2 7.5L11 1" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg></span>';

      row.addEventListener('click', function () {
        toggleSelection(row, variant.id);
      });
      row.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleSelection(row, variant.id);
        }
      });

      frag.appendChild(row);

      // Pre-check the first N items so they're already selected when
      // the popup opens — customer can uncheck if they don't want them.
      if (index < DEFAULT_CHECKED_COUNT) {
        toggleSelection(row, variant.id);
      }
    });

    els.list.appendChild(frag);
    els.list.hidden = false;
  }

  function toggleSelection(row, variantId) {
    var isSelected = row.classList.toggle('is-selected');
    row.setAttribute('aria-checked', String(isSelected));
    if (isSelected) {
      state.selectedVariantIds.add(variantId);
    } else {
      state.selectedVariantIds.delete(variantId);
    }
    updateContinueLabel();
  }

  function updateContinueLabel() {
    var count = state.selectedVariantIds.size;
    els.continueLabel.textContent = count > 0 ? 'Add to Cart (' + count + ')' : 'Add to Cart';
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }

  // ----------------------------------------------------------
  // CART STATE REFRESH (best-effort, no page reload)
  // Patches common header cart-count elements directly, and fires
  // several commonly-used event/pub-sub names in case the theme
  // (or the Smart Cart app) listens for one of them to re-render.
  //
  // IMPORTANT: this returns the fetch promise chain. Callers that
  // need the Smart Cart drawer to show correct, up-to-date items
  // (e.g. after a bulk add) MUST wait for this to resolve before
  // revealing the drawer — otherwise the drawer can render with
  // stale data because it opens before these events are dispatched.
  // ----------------------------------------------------------
  function refreshCartState() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        document
          .querySelectorAll('.cart-count-bubble, [data-cart-count], .cart-count')
          .forEach(function (el) {
            el.textContent = cart.item_count;
          });

        ['cart:updated', 'cart:refresh', 'cart:change', 'cart:build'].forEach(function (name) {
          document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { cart: cart } }));
        });

        if (window.publish && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
          try {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { cart: cart, source: 'upsell-popup' });
          } catch (err) {
            // Non-fatal — theme just doesn't use this pattern.
          }
        }

        return cart;
      })
      .catch(function (err) {
        console.error('[Upsell] Cart state refresh failed:', err);
      });
  }

  // ----------------------------------------------------------
  // SMART CART — DIRECT DOM CONTROL (Shiprocket-specific)
  // The Shiprocket Smart Cart lives inside a Shadow DOM and
  // auto-opens ITSELF whenever it detects a /cart/add.js network
  // call, by setting an inline style on its own container.
  //
  // Earlier version fought this with a MutationObserver that kept
  // re-setting inline styles — since "last write wins" on the same
  // inline style property, both scripts kept overwriting each
  // other in a tight loop, which froze the tab.
  //
  // Fixed approach: inject a small <style> tag INSIDE the same
  // shadow root with a `!important` rule on a class we control.
  // Per the CSS cascade spec, an `!important` stylesheet rule
  // always beats a non-important inline style — no matter how many
  // times Shiprocket's script resets its own inline style. This
  // needs zero JS "fighting" or loops: we just toggle one class.
  //
  // Located via DevTools inspection on this store:
  //   shadow host -> #shiprocket-mini-cart-container -> #mini-cart-drawer
  // If Shiprocket ever changes that markup, this quietly falls
  // back to the configurable openSmartCartFallback() below.
  // ----------------------------------------------------------
  var smartCartContainer = null;   // located once, cached
  var smartCartSuppressed = false; // true while our popup owns the screen
  var smartCartFindTimer = null;
  var HIDE_CLASS = 'upsell-force-hide';

  function injectForceHideStyle(shadowRoot) {
    if (shadowRoot.getElementById && shadowRoot.getElementById('upsell-force-hide-style')) return;
    if (shadowRoot.querySelector('#upsell-force-hide-style')) return;
    var style = document.createElement('style');
    style.id = 'upsell-force-hide-style';
    style.textContent = '.' + HIDE_CLASS + '{display:none!important;}';
    shadowRoot.appendChild(style);
  }

  function findSmartCartContainer() {
    if (smartCartContainer) return smartCartContainer;

    var all = document.querySelectorAll('*');
    for (var i = 0; i < all.length; i++) {
      var root = all[i].shadowRoot;
      if (!root) continue;
      var container = root.querySelector('#shiprocket-mini-cart-container');
      if (container) {
        injectForceHideStyle(root);
        smartCartContainer = container;
        return smartCartContainer;
      }
    }
    return null;
  }

  // Call this the instant WE know an add-to-cart is in flight, so
  // the drawer is caught and hidden before/as Shiprocket reacts.
  // No observer/loop needed — the CSS rule holds by itself.
  function suppressSmartCart() {
    smartCartSuppressed = true;

    var attempts = 0;
    clearInterval(smartCartFindTimer);
    smartCartFindTimer = setInterval(function () {
      attempts++;
      var container = findSmartCartContainer();

      if (container) {
        clearInterval(smartCartFindTimer);
        if (smartCartSuppressed) container.classList.add(HIDE_CLASS);
      } else if (attempts > 25) {
        clearInterval(smartCartFindTimer); // ~5s — give up quietly, fallback covers us
      }
    }, 200);
  }

  // Call this when the customer is ready to see the cart (Skip /
  // Continue / closing the popup any other way, or if an add fails).
  //
  // forceRefresh (optional, default false): pass true when WE added
  // item(s) ourselves via addSelectedProducts(). Just un-hiding the
  // container does NOT make Shiprocket re-fetch its item list — it
  // only refreshes off its OWN "open" trigger, which normally fires
  // automatically when the theme's native Add to Cart runs. Since we
  // added items via our own tagged /cart/add.js calls (deliberately
  // hidden from the passive watcher to avoid double-handling), that
  // trigger never fired — so without this nudge the drawer keeps
  // showing stale (pre-bulk-add) contents until a manual page reload.
  function releaseSmartCart(forceRefresh) {
    if (!smartCartSuppressed) return;
    smartCartSuppressed = false;
    clearInterval(smartCartFindTimer);

    var container = findSmartCartContainer();
    if (container) {
      container.classList.remove(HIDE_CLASS);
      if (forceRefresh) {
        nudgeSmartCartOpen();
      }
    } else {
      openSmartCartFallback();
    }
  }

  // Fires Shiprocket's OWN internal refresh/show functions (found by
  // inspecting `window` in DevTools console — see project notes) so
  // its mini-cart drawer actually re-renders with current cart
  // contents, instead of just becoming visible with stale HTML.
  // Tries each known candidate in order; harmless if a given store's
  // Shiprocket version doesn't expose one of them.
  function nudgeSmartCartOpen() {
    var candidates = [
      'updateMiniCartSection',           // re-renders drawer's item list from latest cart data
      'srShowMiniCartDrawer',            // Shiprocket's own "show drawer" trigger
      'slideShiprocketSmartCartInFrame'  // slide-in trigger, seen alongside the above
    ];

    var calledAny = false;
    candidates.forEach(function (fnName) {
      try {
        if (typeof window[fnName] === 'function') {
          window[fnName]();
          calledAny = true;
        }
      } catch (err) {
        console.error('[Upsell] Shiprocket refresh fn "' + fnName + '" threw:', err);
      }
    });

    if (calledAny) return;

    // Fallback to the configurable method (event / function / click)
    // if none of the known Shiprocket globals were found — e.g. a
    // different Smart Cart version on another store.
    var sc = config.smartCart || {};
    try {
      if (sc.method === 'function') {
        var fn = sc.functionName
          ? sc.functionName.split('.').reduce(function (obj, key) {
              return obj ? obj[key] : undefined;
            }, window)
          : null;
        if (typeof fn === 'function') {
          fn();
          return;
        }
      }
      document.dispatchEvent(new CustomEvent(sc.eventName || 'cart:open', { bubbles: true }));
    } catch (err) {
      console.error('[Upsell] Could not nudge Smart Cart to refresh:', err);
    }
  }

  // ----------------------------------------------------------
  // SMART CART — CONFIGURABLE FALLBACK
  // Only used if the shadow-DOM elements above can't be found
  // (e.g. Shiprocket changes its internal markup).
  // ----------------------------------------------------------
  function openSmartCartFallback() {
    var sc = config.smartCart || {};

    try {
      if (sc.method === 'function') {
        var fn = sc.functionName
          ? sc.functionName.split('.').reduce(function (obj, key) {
              return obj ? obj[key] : undefined;
            }, window)
          : null;
        if (typeof fn === 'function') {
          fn();
          return;
        }
      }

      if (sc.method === 'click') {
        var trigger = document.querySelector(sc.triggerSelector);
        if (trigger) {
          trigger.click();
          return;
        }
      }

      document.dispatchEvent(new CustomEvent(sc.eventName || 'cart:open', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    } catch (err) {
      console.error('[Upsell] Could not open Smart Cart automatically:', err);
    }
  }

  // ----------------------------------------------------------
  // FOOTER ACTIONS
  // ----------------------------------------------------------
  function initFooterActions() {
    els.closeBtn.addEventListener('click', function () {
      closePopup();
    });

    els.overlay.addEventListener('click', function (e) {
      if (e.target === els.overlay) closePopup();
    });

    els.skipBtn.addEventListener('click', function () {
      closePopup();
    });

    els.continueBtn.addEventListener('click', function () {
      if (state.isContinuing) return; // prevent duplicate requests

      if (state.selectedVariantIds.size === 0) {
        closePopup();
        return;
      }

      addSelectedProducts();
    });
  }

  function setContinueLoading(isLoading) {
    els.continueBtn.disabled = isLoading;
    els.skipBtn.disabled = isLoading;
    els.continueSpinner.hidden = !isLoading;
    els.continueLabel.hidden = isLoading;
  }

  function addSelectedProducts() {
    state.isContinuing = true;
    setContinueLoading(true);

    var variantIds = Array.from(state.selectedVariantIds);

    function addOne(variantId) {
      var body = new FormData();
      body.append('id', variantId);
      body.append('quantity', 1);

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          // Tags this as OUR OWN request so the passive watcher above
          // doesn't also react to it a second time (it doesn't stop
          // Shiprocket's own detector, which ignores this header).
          'X-Upsell-Popup': '1'
        },
        body: body
      }).then(function (res) {
        if (!res.ok) return res.json().then(function (err) { throw err; });
        return res.json();
      });
    }

    // IMPORTANT: these must run ONE AT A TIME, not in parallel.
    // Firing several /cart/add.js requests at once (Promise.all) races
    // against Shopify's cart session — the backend can only reliably
    // process one write to a given cart at a time, so with 2+ items
    // selected one of the concurrent requests would randomly fail with
    // a cart-state conflict, which is what triggered the "Could not
    // add selected products. Please try again." alert. Chaining them
    // sequentially (each starts only after the previous resolves)
    // avoids that entirely.
    var chain = variantIds.reduce(function (promise, variantId) {
      return promise.then(function () {
        return addOne(variantId);
      });
    }, Promise.resolve());

    chain
      .then(function () {
        state.isContinuing = false;
        setContinueLoading(false);
        // Wait for the cart to actually refresh (and the cart:updated /
        // cart:refresh events to be dispatched) BEFORE revealing the
        // Smart Cart drawer. Previously closePopup() ran immediately
        // after Promise.all(), racing ahead of refreshCartState() — so
        // the drawer would open with stale data and the newly added
        // items wouldn't show until a manual page refresh.
        return refreshCartState();
      })
      .then(function () {
        // Not calling closePopup() here on purpose — it calls the
        // plain releaseSmartCart() (no force-refresh). We need the
        // forceRefresh=true path so Shiprocket actually re-fetches
        // its item list instead of just becoming visible again with
        // stale data.
        els.overlay.classList.remove('is-open');
        els.overlay.setAttribute('aria-hidden', 'true');
        document.documentElement.style.overflow = '';
        releaseSmartCart(true); // true = force Shiprocket to re-fetch its cart
      })
      .catch(function (err) {
        state.isContinuing = false;
        setContinueLoading(false);
        releaseSmartCart();
        console.error('[Upsell] Adding selected products failed:', err);
        alert((err && err.description) || 'Could not add selected products. Please try again.');
      });
  }

  // ----------------------------------------------------------
  // INIT
  // ----------------------------------------------------------
  function init() {
    cacheEls();
    initCartAddWatcher();
    initFooterActions();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();